import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { Wallet, CaretRight } from '@phosphor-icons/react'
import { getTurkishDativeSuffix, formatAmountWithCurrency } from '@/utils/abacus'
import { cn } from '@/lib/utils'
import { springTransition } from '@/themes/animations'

interface UnpaidDebt {
  groupId: string
  groupName: string
  receiverName: string
  amount: number
  currency: string
  dueDate: string
  daysOverdue: number
  currentCycle: number
}

interface UnpaidDebtsWidgetProps {
  unpaidDebts: UnpaidDebt[]
}

export function UnpaidDebtsWidget({ unpaidDebts }: UnpaidDebtsWidgetProps) {
  const navigate = useNavigate()

  return (
    <AnimatePresence mode="popLayout">
      {unpaidDebts.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, height: 0, marginBottom: 0 }}
          transition={springTransition}
          className="will-change-[height,opacity,transform] overflow-hidden"
        >
          <div className="flex items-center justify-between mb-4 px-1 mt-7">
            <h3 className="text-[15px] font-bold uppercase tracking-[0.18em] text-espresso/80">
              Ödeme Durumu
            </h3>
            <span className="flex items-center gap-1.5 bg-espresso/[0.04] text-espresso/80 p-2 rounded-full border border-espresso/10">
              <Wallet size={15} weight="duotone" />
            </span>
          </div>
          <div className="flex flex-col gap-2">
            {unpaidDebts.map((debt, index) => (
              <motion.div
                key={debt.groupId}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springTransition, delay: index * 0.05 }}
                exit={{ opacity: 0, y: -8 }}
                className="bg-white border rounded-2xl py-3 px-4 flex items-center justify-between shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-shadow cursor-pointer hover:shadow-md border-stone-200 relative overflow-hidden active:scale-[0.98]"
                onClick={() => navigate(`/groups/${debt.groupId}/payment?cycle=${debt.currentCycle}`)}
              >
                {/* Sol kenar accent çizgisi */}
                <div className={cn(
                  'absolute left-0 top-0 bottom-0 w-[3px]',
                  debt.daysOverdue > 0 ? 'bg-red-400' : 'bg-brand-gold'
                )} />

                <div className="flex items-center gap-3">
                  <div className={cn(
                    'w-8 h-8 rounded-xl border flex items-center justify-center',
                    debt.daysOverdue > 0
                      ? 'bg-red-50 border-red-100'
                      : 'bg-amber-50 border-amber-100'
                  )}>
                    <Wallet size={16} weight="bold" className={debt.daysOverdue > 0 ? 'text-red-500' : 'text-brand-gold'} />
                  </div>
                  <div>
                    <span className="text-[13px] font-semibold text-espresso block">{debt.groupName}</span>
                    <span className="text-[10.5px] text-stone-400 block mt-0.5">
                      {debt.receiverName}{getTurkishDativeSuffix(debt.receiverName)} · {formatAmountWithCurrency(debt.amount, debt.currency)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <div className="text-right">
                    {debt.daysOverdue > 0 ? (
                      <span className="text-[11px] font-bold text-red-500 leading-none">
                        {debt.daysOverdue} gün gecikme
                      </span>
                    ) : (
                      <span className="text-[11px] font-medium text-stone-400 leading-none">
                        {new Date(debt.dueDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}'a kadar
                      </span>
                    )}
                  </div>
                  <CaretRight size={14} weight="bold" className="text-slate-300" />
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>
      )}
    </AnimatePresence>
  )
}
